import type {
  UploadFile,
  UploadState,
} from '@pflegenavi/shared-frontend/platform';
import {
  makeApiProvider,
  useApiQuery,
  usePrefetchApiQuery,
  useUploadFile,
} from '@pflegenavi/shared-frontend/platform';
import type {
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  replaceEqualDeep,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import type {
  CancelManualDepositDto,
  CancelReceiptDto,
  CreateReceiptBatchJobAction,
  CreateReceiptDto,
  FilteredTransactionChangesResultDto,
  GetFilteredBalanceChangesQueryParams,
  GetReceiptBatchesQueryDto,
  GetTotalResidentBalanceQueryParams,
  GetTransactionsWithReceiptDto,
  IReceiptType,
  ManualDepositDto,
  ReceiptBatchPaginatedDto,
  ReceiptFull,
  TotalResidentBalanceResultDto,
  TransactionSourceType,
  UpdateTransactionDto,
} from '@pflegenavi/shared/api';
import type { PaginatedResultSubset } from '@pflegenavi/shared/utils';
import isEqual from 'lodash.isequal';
import { createContext, useCallback } from 'react';
import {
  CASH_LIST_CONFIGURATION_KEY,
  CASH_TRANSACTION_GROUP_KEY,
} from '../cash-management/queryKeys';
import { RECEIPT_BATCH_PAYMENT_STATUS } from '../payment';
import {
  GET_RECEIPT_KEY,
  RECEIPTS_INDEX_KEY,
} from '../receipt-phoenix/queryKeys';
import type { ReceiptPhoenix } from '../receipt-phoenix/api';
import { RECEIPT_BATCH_LIST_PHOENIX_KEY } from '../receipt-batch-phoenix/queryKeys';
import { PLIANT_TRANSACTIONS_KEY } from '../pliant-phoenix/queryKeys';
import { getFinanceKey } from '../reporting-phoenix';
import { invalidateAllResidentBalance } from '../reporting/queryKeys';
import {
  ALL_SHOW_RESIDENT_KEY,
  RESIDENTS_PHOENIX_KEY,
} from '../resident-phoenix/queryKeys';
import { TRANSACTIONS_PHOENIX_KEY } from '../transaction-phoenix/queryKeys';
import { RECEIPTS_AND_RECEIPT_BATCHES_PHOENIX_KEY } from '../receipt-phoenix/queryKeys';
import {
  CASH_TRANSACTION_GROUP_PHOENIX_KEY,
  CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY,
} from '../cash-management-phoenix/queryKeys';
import {
  RESIDENTS_KEY,
  invalidatePendingReceiptCount,
  invalidateResident,
} from '../resident/queryKeys';
import { usePrefetchServiceProviders } from '../service-provider';
import type { ITransactionApi } from './api';
import { TransactionApi } from './api';
import {
  CANCEL_RECEIPT_KEY,
  CREATE_RECEIPT_BATCH_JOB,
  CREATE_RECEIPT_KEY,
  DELETE_TRANSACTION_KEY,
  FILTERED_BALANCE_CHANGES_PERIOD_KEY,
  MANUAL_DEPOSIT_CANCELLATION_KEY,
  MANUAL_DEPOSIT_KEY,
  RECEIPTS_AND_RECEIPT_BATCHES_KEY,
  RECEIPTS_AND_RECEIPT_BATCHES_PREFIX,
  RECEIPTS_KEY,
  RECEIPTS_KEY_NURSING_HOME,
  RECEIPT_BATCH_KEY,
  RECEIPT_BATCH_LIST_KEY,
  RECEIPT_BATCH_LIST_PAGINATED_KEY,
  SUBMIT_RECEIPTS_KEY,
  TOTAL_BALANCE_PERIOD_KEY,
  TRANSACTIONS_PAGINATED_KEY,
  TRANSACTION_TYPES_KEY,
  UPDATE_TRANSACTION_KEY,
  getReceiptImagesKey,
  getReceiptKey,
  getTransactionsAsFamilyMemberKey,
  getTransactionsKey,
} from './queryKeys';

export {
  isReceiptBatchWithInactiveServiceProviderError,
  isReceiptWithInactiveServiceProviderError,
} from './api';
export type { ITransactionApi } from './api';

const ApiContext = createContext<ITransactionApi | undefined>(undefined);
const { useApi: useTransactionApi, ApiProvider: TransactionApiProvider } =
  makeApiProvider({
    name: 'Transaction',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new TransactionApi(tenantId, auth, apiUrl),
  });

export {
  ApiContext as TransactionApiContext,
  TransactionApiProvider,
  useTransactionApi,
};

export const useTotalBalancePeriod = (
  params: GetTotalResidentBalanceQueryParams & {
    nursingHomeId: string | undefined;
  },
  options?: Omit<
    UseQueryOptions<TotalResidentBalanceResultDto, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<TotalResidentBalanceResultDto, unknown> => {
  return useApiQuery(
    useTransactionApi,
    TOTAL_BALANCE_PERIOD_KEY(params),
    (api) => {
      if (!params.nursingHomeId) {
        throw new Error('nursingHomeId is required');
      }
      return api.getTotalBalancePeriod({
        params: {
          ...params,
          nursingHomeId: params.nursingHomeId,
        },
      });
    },
    {
      enabled: params.nursingHomeId !== undefined,
      ...options,
    }
  );
};

export const useFilteredBalanceChangesPeriod = (
  params: GetFilteredBalanceChangesQueryParams & {
    nursingHomeId: string | undefined;
  },
  options?: Omit<
    UseQueryOptions<FilteredTransactionChangesResultDto, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<FilteredTransactionChangesResultDto, unknown> => {
  return useApiQuery(
    useTransactionApi,
    FILTERED_BALANCE_CHANGES_PERIOD_KEY(params),
    (api) => {
      if (!params.nursingHomeId) {
        throw new Error('nursingHomeId is required');
      }
      return api.getFilteredBalanceChangesPeriod({
        params: {
          ...params,
          nursingHomeId: params.nursingHomeId,
        },
      });
    },
    {
      enabled: params.nursingHomeId !== undefined,
      ...options,
    }
  );
};

const mapGetTransactionWithReceiptResponseToDto = (
  responseElement: GetTransactionsWithReceiptDto
): GetTransactionsWithReceiptDto => {
  return {
    ...responseElement,
    date: new Date(responseElement.date),
    receiptDate: responseElement.receiptDate
      ? new Date(responseElement.receiptDate)
      : undefined,
    cancelledSubmissionDate: responseElement.cancelledSubmissionDate
      ? new Date(responseElement.cancelledSubmissionDate)
      : undefined,
  };
};

const makeTransactionQueryOptions = (
  sourceTypes: readonly TransactionSourceType[],
  options?: Omit<
    UseQueryOptions<GetTransactionsWithReceiptDto[], unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryOptions<GetTransactionsWithReceiptDto[], unknown> => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const select = useCallback(
    (
      data: GetTransactionsWithReceiptDto[]
    ): GetTransactionsWithReceiptDto[] => {
      if (!data) {
        return [];
      }
      let response;
      if (sourceTypes.length > 0) {
        response = data.filter((row) => sourceTypes?.includes(row.sourceType));
      } else {
        response = data;
      }
      return response.map((row) =>
        mapGetTransactionWithReceiptResponseToDto(row)
      );
    },
    [sourceTypes]
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isDataEqual = useCallback(
    (
      oldData: GetTransactionsWithReceiptDto[] | undefined,
      newData: GetTransactionsWithReceiptDto[]
    ) => {
      return oldData ? oldData.length === newData.length : false;
    },
    []
  );

  return {
    // Taking advantage of transactions being append only - e.g. there can't be any changes in-between
    // @ts-expect-error // structuralSharing expects "unknown"
    structuralSharing: (
      oldData: GetTransactionsWithReceiptDto[] | undefined,
      newData: GetTransactionsWithReceiptDto[]
    ) => {
      const shouldKeepOld = isDataEqual(oldData, newData);
      return shouldKeepOld ? oldData : replaceEqualDeep(oldData, newData);
    },
    select: select,
    ...options,
  };
};

export const useTransactionsForFamilyMember = (
  residentId: string | undefined,
  sourceTypes: readonly TransactionSourceType[] = [],
  familyMemberId?: string | null,
  options?: Omit<
    UseQueryOptions<GetTransactionsWithReceiptDto[], unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetTransactionsWithReceiptDto[], unknown> => {
  const transactionQueryOptions = makeTransactionQueryOptions(sourceTypes, {
    ...options,
    enabled:
      options?.enabled === undefined
        ? residentId !== undefined
        : options?.enabled && residentId !== undefined,
  });
  const api = useTransactionApi(true);
  return useApiQuery(
    () => api,
    getTransactionsAsFamilyMemberKey(
      residentId ?? 'undefined',
      undefined,
      familyMemberId
    ),
    (api) => {
      if (!residentId || !api) {
        throw new Error('residentId is undefined');
      }
      return api.getTransactionsAsFamilyMember(
        residentId,
        undefined,
        familyMemberId
      );
    },
    transactionQueryOptions
  );
};

export const useReceipt = (
  transactionId: string,
  nursingHomeId: string | undefined,
  queryOptions?: {
    optionalFields?: {
      linkedTransactionGroups?: boolean;
      receiptBatch?: boolean;
    };
  },
  options?: Omit<UseQueryOptions<ReceiptFull, unknown>, 'queryFn' | 'queryKey'>
): UseQueryResult<ReceiptFull, unknown> => {
  // Always call hooks unconditionally to comply with Rules of Hooks
  // Pass enabled flag to prevent prefetching when query is disabled
  const shouldPrefetch = options?.enabled !== false;
  usePrefetchTransactionTypes(shouldPrefetch);
  usePrefetchServiceProviders(nursingHomeId, shouldPrefetch);

  const select = useCallback((data: ReceiptFull): ReceiptFull => {
    return {
      ...data,
      date: new Date(data.date),
      created_on: new Date(data.created_on),
      submissionDate: data.submissionDate
        ? new Date(data.submissionDate)
        : undefined,
      settlement_date: data.settlement_date
        ? new Date(data.settlement_date)
        : null,
      embargoDate: data.embargoDate ? new Date(data.embargoDate) : undefined,
    };
  }, []);

  return useApiQuery(
    useTransactionApi,
    getReceiptKey(transactionId, queryOptions?.optionalFields),
    (api) => api.getOne(transactionId, queryOptions),
    {
      select,
      ...options,
    }
  );
};

export const useCreateReceipt = (): UseMutationResult<
  ReceiptFull,
  unknown,
  CreateReceiptDto
> => {
  const api = useTransactionApi();
  const queryClient = useQueryClient();
  const result = useMutation<ReceiptFull, unknown, CreateReceiptDto>({
    mutationKey: CREATE_RECEIPT_KEY,
    mutationFn: (data) => api.create(data),
    onSuccess: async () => {
      return await Promise.all([
        queryClient.invalidateQueries({ queryKey: RECEIPTS_KEY }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_PREFIX,
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_INDEX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
        }),
        invalidatePendingReceiptCount(queryClient),
      ]);
    },
  });
  return result;
};

interface UpdateTransactionData extends UpdateTransactionDto {
  groupId?: string;
}

/**
 * Applies UpdateTransactionDto changes to a ReceiptPhoenix object.
 * Handles field name transformations between legacy and Phoenix formats.
 */
function applyUpdateToPhoenixReceipt(
  phoenixReceipt: ReceiptPhoenix,
  update: UpdateTransactionDto
): ReceiptPhoenix {
  const updated = { ...phoenixReceipt };

  // Map UpdateTransactionDto fields to ReceiptPhoenix format
  if (update.amountInCent !== undefined && update.amountInCent !== null) {
    updated.amount = update.amountInCent;
  }
  if (update.additionalFeeAmountInCent !== undefined) {
    updated.additional_fee_amount = update.additionalFeeAmountInCent ?? 0;
  }
  if (update.includedFeeAmountInCent !== undefined) {
    updated.included_fee_amount = update.includedFeeAmountInCent ?? 0;
  }
  if (update.date !== undefined && update.date !== null) {
    updated.receipt_date = update.date;
  }
  if (update.notes !== undefined) {
    updated.notes = update.notes;
  }
  if (update.receipt_not_available !== undefined) {
    updated.receipt_not_available = update.receipt_not_available;
  }
  if (update.receipt_image_ids !== undefined) {
    updated.receipt_image_ids = update.receipt_image_ids;
  }

  return updated;
}

export const useUpdateTransaction = (queryInvalidationParams?: {
  nursingHomeId?: string;
}): UseMutationResult<Partial<ReceiptFull>, unknown, UpdateTransactionData> => {
  const api = useTransactionApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    Partial<ReceiptFull>,
    unknown,
    UpdateTransactionData,
    {
      previousReceipt?: ReceiptFull;
      previousPhoenixData?: { data: ReceiptPhoenix };
    }
  >({
    mutationKey: UPDATE_TRANSACTION_KEY,
    mutationFn: (data) => api.update(data),
    onMutate: async (data) => {
      await queryClient.cancelQueries({
        queryKey: getReceiptKey(data.receipt_id),
      });
      await queryClient.cancelQueries({
        queryKey: GET_RECEIPT_KEY(data.receipt_id),
      });
      const receipts = queryClient.getQueriesData<ReceiptFull>({
        type: 'all',
        queryKey: getReceiptKey(data.receipt_id),
        exact: false,
        stale: true,
      });
      const [, previousReceipt] = receipts?.find(
        ([, receipt]) => receipt?.id === data.receipt_id
      ) ?? ['', undefined];

      // Get previous Phoenix data
      const phoenixQueries = queryClient.getQueriesData<{
        data: ReceiptPhoenix;
      }>({
        type: 'all',
        queryKey: GET_RECEIPT_KEY(data.receipt_id),
        exact: false,
        stale: true,
      });
      const phoenixQueryResult = phoenixQueries?.find(
        ([, queryData]) => queryData?.data?.id === data.receipt_id
      );
      const [, previousPhoenixData] = phoenixQueryResult ?? ['', undefined];

      if (!previousReceipt && !previousPhoenixData) {
        return { previousReceipt, previousPhoenixData };
      }

      // Update legacy cache (if it exists)
      if (previousReceipt) {
        const newReceipt = { ...previousReceipt, ...data };

        const nullable = [
          'date',
          'resident_id',
          'amount',
          'amountInCent',
          'notes',
          'receipt_type_id',
          'service_provider',
        ] as const;
        for (const key of nullable) {
          if (newReceipt[key] === null) {
            delete newReceipt[key];
          }
        }

        queryClient.setQueryData(getReceiptKey(data.receipt_id), newReceipt);
      }

      // Update Phoenix cache (if it exists) - MUST wrap in { data: ... }
      // Use transformation function to handle field name differences
      if (previousPhoenixData?.data) {
        const updatedPhoenixData = {
          data: applyUpdateToPhoenixReceipt(previousPhoenixData.data, data),
        };
        queryClient.setQueryData(
          GET_RECEIPT_KEY(data.receipt_id),
          updatedPhoenixData
        );
      }

      return { previousReceipt, previousPhoenixData };
    },
    onError: (err, newTodo, context) => {
      if (context) {
        // Rollback legacy query
        if (context.previousReceipt) {
          queryClient.setQueryData(
            getReceiptKey(newTodo.receipt_id),
            context.previousReceipt
          );
        }
        // Rollback Phoenix query
        if (context.previousPhoenixData) {
          queryClient.setQueryData(
            GET_RECEIPT_KEY(newTodo.receipt_id),
            context.previousPhoenixData
          );
        }
      }
    },
    onSuccess: async ({ id }, { groupId }) => {
      if (groupId) {
        await queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_KEY(groupId),
        });
        await queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_PHOENIX_KEY(groupId),
        });
      }
      if (id) {
        await queryClient.invalidateQueries({ queryKey: getReceiptKey(id) });
        await queryClient.invalidateQueries({ queryKey: GET_RECEIPT_KEY(id) });
      }

      await queryClient.invalidateQueries({
        queryKey: RECEIPTS_KEY_NURSING_HOME(
          queryInvalidationParams?.nursingHomeId
        ),
      });
      await queryClient.invalidateQueries({
        queryKey: RECEIPTS_AND_RECEIPT_BATCHES_KEY(
          queryInvalidationParams?.nursingHomeId
        ),
      });
      await queryClient.invalidateQueries({
        queryKey: RECEIPTS_AND_RECEIPT_BATCHES_PHOENIX_KEY(
          queryInvalidationParams?.nursingHomeId
        ),
      });
      await queryClient.invalidateQueries({
        queryKey: RECEIPTS_INDEX_KEY(),
      });
      await queryClient.invalidateQueries({
        queryKey: TRANSACTIONS_PHOENIX_KEY(),
      });
      return await queryClient.invalidateQueries({ queryKey: RECEIPTS_KEY });
    },
  });
  return result;
};

export const useCancelReceipt = (
  receiptId: string,
  nursingHomeId?: string
): UseMutationResult<void, unknown, CancelReceiptDto> => {
  const api = useTransactionApi();
  const queryClient = useQueryClient();
  return useMutation<void, unknown, CancelReceiptDto>({
    mutationKey: CANCEL_RECEIPT_KEY(receiptId),
    mutationFn: (data) => api.cancel(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getReceiptKey(receiptId),
      });
      await queryClient.invalidateQueries({
        queryKey: GET_RECEIPT_KEY(receiptId),
      });
      await queryClient.invalidateQueries({
        queryKey: RESIDENTS_KEY(nursingHomeId),
      });
      await invalidateAllResidentBalance(queryClient, nursingHomeId);
      await queryClient.invalidateQueries({
        queryKey: CANCEL_RECEIPT_KEY(receiptId),
      });
      return await queryClient.invalidateQueries({
        queryKey: TRANSACTIONS_PHOENIX_KEY(),
      });
    },
  });
};

export const useReceiptTypes = (
  options?: Omit<
    UseQueryOptions<IReceiptType[], unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<IReceiptType[], unknown> => {
  return useApiQuery(
    useTransactionApi,
    TRANSACTION_TYPES_KEY,
    (api) => api.getTypes(),
    options
  );
};

export const usePrefetchTransactionTypes = (enabled = true): void => {
  const getTransactionTypes = useCallback(
    (api: ITransactionApi) => api.getTypes(),
    []
  );

  return usePrefetchApiQuery(
    useTransactionApi,
    TRANSACTION_TYPES_KEY,
    getTransactionTypes,
    enabled
  );
};

export const useDeleteReceipt = (invalidationData?: {
  nursingHomeId?: string;
}): {
  deleteTransaction: UseMutationResult<void, unknown, string, unknown>;
} => {
  const api = useTransactionApi();
  const queryClient = useQueryClient();
  const deleteTransaction = useMutation({
    mutationKey: DELETE_TRANSACTION_KEY,
    mutationFn: (id: string) => api.delete(id),
    onSettled: async () => {
      return await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_KEY_NURSING_HOME(invalidationData?.nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_KEY(
            invalidationData?.nursingHomeId
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_INDEX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
      ]);
    },
  });
  return {
    deleteTransaction,
  };
};

export const useInvalidateReceiptsQueries: () => (
  nursingHomeId: string | undefined
) => Promise<void[]> = () => {
  const queryClient = useQueryClient();

  return (nursingHomeId: string | undefined) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ALL_SHOW_RESIDENT_KEY() }),
      queryClient.invalidateQueries({ queryKey: RECEIPTS_KEY }),
      queryClient.invalidateQueries({
        queryKey: getFinanceKey(nursingHomeId),
      }),
      invalidateAllResidentBalance(queryClient, nursingHomeId),
      queryClient.invalidateQueries({
        queryKey: RESIDENTS_KEY(nursingHomeId),
      }),
      queryClient.invalidateQueries({
        queryKey: RESIDENTS_PHOENIX_KEY(nursingHomeId ?? ''),
      }),
      queryClient.invalidateQueries({
        queryKey: RECEIPTS_AND_RECEIPT_BATCHES_KEY(nursingHomeId),
      }),
      queryClient.invalidateQueries({
        queryKey: RECEIPTS_AND_RECEIPT_BATCHES_PHOENIX_KEY(nursingHomeId),
      }),
      queryClient.invalidateQueries({
        queryKey: RECEIPTS_INDEX_KEY(),
      }),
      queryClient.invalidateQueries({
        queryKey: TRANSACTIONS_PHOENIX_KEY(),
      }),
      queryClient.invalidateQueries({
        queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
      }),
      queryClient.invalidateQueries({
        queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
      }),
      queryClient.invalidateQueries({
        queryKey: PLIANT_TRANSACTIONS_KEY(),
      }),
    ]);
};

export const useSubmitReceipts = (): {
  submitReceipts: UseMutationResult<
    Array<{
      id: string;
    }>,
    unknown,
    {
      receiptIds: string[];
      nursingHomeId?: string;
    },
    unknown
  >;
} => {
  const api = useTransactionApi();
  const invalidateQueries = useInvalidateReceiptsQueries();
  const submitReceipts = useMutation<
    Array<{
      id: string;
    }>,
    unknown,
    {
      receiptIds: string[];
      nursingHomeId?: string;
    },
    unknown
  >({
    mutationKey: SUBMIT_RECEIPTS_KEY,
    mutationFn: (params) => api.submit(params.receiptIds),
    onSettled: async (_data, _error, params) => {
      await invalidateQueries(params.nursingHomeId);
    },
  });
  return {
    submitReceipts,
  };
};

export const useReceiptImages = (
  receiptImageIds?: string[]
): UseQueryResult<
  | {
      [key: string]: string | undefined;
    }
  | undefined,
  unknown
> => {
  return useApiQuery(
    useTransactionApi,
    getReceiptImagesKey(receiptImageIds?.toString()),
    (api) => {
      if (receiptImageIds && receiptImageIds?.length > 0) {
        return api.getReceiptImageUrls(receiptImageIds);
      }
      return Promise.resolve({});
    },
    {
      enabled: receiptImageIds !== undefined, // && receiptImageIds.length !== 0,
      gcTime: 0,
      refetchOnWindowFocus: false,
      structuralSharing: (oldData, newData) =>
        isEqual(oldData, newData)
          ? oldData
          : replaceEqualDeep(oldData, newData),
    }
  );
};

const RECEIPT_IMAGE_URLS_AND_TYPE_KEY = (imageIds?: string[]) => [
  'RECEIPT_IMAGE_URLS_AND_TYPE_KEY',
  ...(imageIds ?? []),
];
export const useReceiptImageUrlsAndType = (
  imageIds?: string[]
): UseQueryResult<
  Array<{
    imageId: string;
    imageUri?: string;
    previewUri?: string;
    fileType?: string;
  }>,
  unknown
> => {
  return useApiQuery(
    useTransactionApi,
    RECEIPT_IMAGE_URLS_AND_TYPE_KEY(imageIds),
    async (api) => {
      if (imageIds && imageIds?.length > 0) {
        const imageIdsToUris = await api.getReceiptImageUrls(imageIds);
        const imageIdsToTypes = await Promise.all(
          imageIds.map(async (imageId) => {
            const imageUri = imageIdsToUris?.[imageId];
            if (!imageUri) {
              return {
                imageId,
                imageUri: undefined,
                previewUri: undefined,
                fileType: undefined,
              };
            }

            const response = await fetch(imageUri);
            const extractedFileType =
              response.headers.get('content-type') ?? 'type-unknown';

            return {
              imageId,
              imageUri,
              previewUri: imageUri,
              fileType: extractedFileType,
            };
          })
        );

        return imageIdsToTypes;
      }
      return undefined;
    },
    {
      enabled: imageIds?.length ? imageIds.length > 0 : false,
    }
  );
};

export const useUploadReceiptImage: (
  uuid: () => string,
  computeMd5Hash: (file: File) => Promise<string>
) => {
  upload: UploadFile;
  uploadState: UploadState | undefined;
} = (uuid, computeMd5Hash) => {
  const api = useTransactionApi();

  const { upload, uploadState } = useUploadFile({
    uuid,
    getUploadUrl: api.getUploadUrl.bind(api),
    computeMd5Hash,
  });
  return {
    upload,
    uploadState,
  };
};

export const useGetUploadUrl: () => (
  receiptId: string,
  fileType: string,
  md5Hash: string
) => Promise<string | undefined> = () => {
  const api = useTransactionApi();
  return useCallback(
    (receiptId: string, fileType: string, md5Hash: string) => {
      return api.getUploadUrl(receiptId, fileType, md5Hash);
    },
    [api]
  );
};

export const useCreateManualDeposit = (): UseMutationResult<
  void,
  unknown,
  {
    data: ManualDepositDto;
    nursingHomeId?: string;
  }
> => {
  const api = useTransactionApi();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    unknown,
    {
      data: ManualDepositDto;
      nursingHomeId?: string;
    }
  >({
    mutationKey: MANUAL_DEPOSIT_KEY,
    mutationFn: (params) => api.manualDeposit(params.data),
    onSuccess: (_, params) => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: getTransactionsKey(params.data.resident_id),
        }),
        invalidateAllResidentBalance(queryClient, params.nursingHomeId),
        invalidateResident(
          queryClient,
          params.data.resident_id,
          params.nursingHomeId
        ),
        queryClient.invalidateQueries({
          queryKey: TRANSACTIONS_PAGINATED_KEY({
            nursingHomeId: params.nursingHomeId,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: getFinanceKey(params.nursingHomeId),
        }),
      ]);
    },
  });
};

export const useCancelManualDeposit = (): UseMutationResult<
  void,
  unknown,
  {
    transactionId: string;
    residentId: string;
    cashTransaction?: CancelManualDepositDto;
    nursingHomeId?: string;
  }
> => {
  const api = useTransactionApi();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    unknown,
    {
      transactionId: string;
      residentId: string;
      cashTransaction?: CancelManualDepositDto;
      nursingHomeId?: string;
    }
  >({
    mutationKey: MANUAL_DEPOSIT_CANCELLATION_KEY,
    mutationFn: ({ transactionId, cashTransaction }) =>
      api.cancelManualDeposit({
        transactionId,
        cashTransaction,
      }),
    onSuccess: (_, data) => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: getTransactionsKey(data.residentId),
        }),
        invalidateResident(queryClient, data.residentId, data.nursingHomeId),
        invalidateAllResidentBalance(queryClient, data.nursingHomeId),
        queryClient.invalidateQueries({
          queryKey: RESIDENTS_KEY(data.nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: TRANSACTIONS_PAGINATED_KEY({
            nursingHomeId: data.nursingHomeId,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_CONFIGURATION_KEY(data.nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: getFinanceKey(data.nursingHomeId),
        }),
      ]);
    },
  });
};

export function useGetReceiptBatchListPaginated<
  TData = PaginatedResultSubset<ReceiptBatchPaginatedDto>
>(
  params: GetReceiptBatchesQueryDto & {
    nursingHomeId: string | undefined;
  },
  options: Omit<
    UseQueryOptions<
      PaginatedResultSubset<ReceiptBatchPaginatedDto>,
      unknown,
      TData
    >,
    'queryKey' | 'queryFn'
  > = {}
): UseQueryResult<TData, unknown> {
  const select = useCallback(
    (data: PaginatedResultSubset<ReceiptBatchPaginatedDto>): TData => {
      const result = {
        ...data,

        data: data.data.map((batch) => ({
          ...batch,
          receiptImageIds: batch.receiptImageIds ?? [],
          receiptDate: batch.receiptDate
            ? new Date(batch.receiptDate)
            : undefined,
          submissionDate: batch.submissionDate
            ? new Date(batch.submissionDate)
            : undefined,
          minReceiptDate: batch.minReceiptDate
            ? new Date(batch.minReceiptDate)
            : undefined,
          maxReceiptDate: batch.maxReceiptDate
            ? new Date(batch.maxReceiptDate)
            : undefined,
        })),
      };

      // @ts-expect-error // type depends on if options.select is passed or not. Hard to type correctly
      return options?.select ? options.select(result) : result;
    },
    [options]
  );
  return useApiQuery(
    useTransactionApi,
    RECEIPT_BATCH_LIST_PAGINATED_KEY(params),
    (api) => {
      if (params.nursingHomeId === undefined) {
        throw Error('nursingHomeId is undefined');
      }
      if (!api) {
        throw Error('api is undefined');
      }
      return api.getReceiptBatchListPaginated({
        params: {
          ...params,
          nursingHomeId: params.nursingHomeId,
        },
      });
    },
    {
      ...options,
      select,
      enabled: options.enabled ?? params.nursingHomeId !== undefined,
    }
  );
}

export const useCreateReceiptBatchJob = (): UseMutationResult<
  {
    id: string;
  },
  unknown,
  {
    batchId: string;
    action?: CreateReceiptBatchJobAction;
    nursingHomeId: string | undefined;
  }
> => {
  const api = useTransactionApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    {
      id: string;
    },
    unknown,
    {
      batchId: string;
      action?: CreateReceiptBatchJobAction;
      nursingHomeId: string | undefined;
    }
  >({
    mutationKey: CREATE_RECEIPT_BATCH_JOB,
    mutationFn: (data) =>
      api.createReceiptBatchJob({
        params: { receiptBatchId: data.batchId },
        body: {
          action: data.action,
        },
      }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_KEY(variables.nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PAGINATED_KEY({
            nursingHomeId: variables.nursingHomeId,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_KEY(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PAYMENT_STATUS(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_KEY(variables.nursingHomeId),
        }),
      ]);
    },
  });
  return result;
};
